import { defineStore } from 'pinia'
import { parseJSON } from 'date-fns'
import { useAuthStore } from './authStore'
import tooPublishers from '../api/tooPublishers'
import tooManagement from '../api/tooManagement'

const parseVacancyCreationDateAsUTC = (vacancy) => ({
  ...vacancy,
  creationDate: parseJSON(vacancy.creationDate),
})

const defaultSearchParams = {
  vacancy: '',
  siteIds: [],
  companyIds: [],
  publisherIds: [],
  discipline: [],
  workCategory: [],
  employmentType: [],
  education: [],
  workExperience: [],
  region: [],
  geoLocation: {
    latitude: null,
    longitude: null,
    distance: 10,
    label: '',
  },
  salaryMin: null,
  salaryMax: null,
  salaryPeriod: 'BPM',
  topItem: null,
  staffingPost: null,
  includeArchived: false,
  createdBefore: null,
  createdAfter: null,
  updatedBefore: null,
  updatedAfter: null,
  publication: {
    startsBefore: null,
    startsAfter: null,
    endsBefore: null,
    endsAfter: null,
    scopes: ['EXTERNAL', 'INTERNAL', 'REGIONAL'],
  },
}

export const useVacancyListStore = defineStore('vacancyListStore', {
  state: () => ({
    isLoading: false,
    error: null,
    vacancies: [],
    searchTerm: '',
    filter: window.structuredClone(defaultSearchParams),
    sortBy: 'id',
    sortOrder: 'desc',
    page: {
      number: 1,
      size: 10,
      totalElements: 0,
      totalPages: 0,
    },
  }),
  getters: {
    isDefaultSearch(state) {
      return (
        JSON.stringify(defaultSearchParams) === JSON.stringify(state.filter)
      )
    },
  },
  actions: {
    async load() {
      // Reset state
      this.vacancies = []
      this.error = null

      this.isLoading = true
      const authStore = useAuthStore()

      try {
        let vacancies
        const page = {
          page: this.page.number,
          size: this.page.size,
          sortBy: this.sortBy,
          sortOrder: this.sortOrder,
        }

        if (authStore.isAdmin) {
          const filter = { ...this.filter }
          filter.siteIds = filter.siteIds.map((site) => site.id)
          filter.companyIds = filter.companyIds.map((company) => company.id)
          filter.publisherIds = filter.publisherIds.map(
            (publisher) => publisher.id,
          )

          const tagGroups = [
            'education',
            'discipline',
            'workCategory',
            'workExperience',
            'employmentType',
            'region',
          ]
          tagGroups.forEach((tagGroup) => {
            filter[tagGroup] = filter[tagGroup].map((tag) => tag.key)
          })
          vacancies = await tooManagement.searchVacancies(filter, page)
        } else {
          const params = {
            filter: this.searchTerm,
            includeArchived: this.filter.includeArchived,
            ...page,
          }
          vacancies = await tooPublishers.getVacancies(params)
        }
        // Check if this is the page we requested.
        // Useful if a user requests the next page before the previous page was loaded.
        if (vacancies.meta.page.number === this.page.number) {
          this.vacancies = vacancies.data.map(parseVacancyCreationDateAsUTC)
          this.page = vacancies.meta.page
        }
      } catch (error) {
        this.error = error
      } finally {
        this.isLoading = false
      }
    },
    async loadAll() {
      const pageSize = 200
      const totalPages = Math.min(
        Math.ceil(10000 / pageSize), // We fetch 10000 items max
        Math.ceil(this.page.totalElements / pageSize), // Determine how many requests we need to fetch all the elements
      )
      const pagePromises = []

      const authStore = useAuthStore()

      const page = {
        page: 1,
        size: pageSize,
        sortBy: this.sortBy,
        sortOrder: this.sortOrder,
      }

      for (let i = 0; i < totalPages; i++) {
        page.page = i + 1

        if (authStore.isAdmin) {
          const filter = { ...this.filter }
          filter.siteIds = filter.siteIds.map((site) => site.id)
          filter.companyIds = filter.companyIds.map((company) => company.id)
          filter.publisherIds = filter.publisherIds.map(
            (publisher) => publisher.id,
          )

          const tagGroups = [
            'education',
            'discipline',
            'workCategory',
            'workExperience',
            'employmentType',
            'region',
          ]
          tagGroups.forEach((tagGroup) => {
            filter[tagGroup] = filter[tagGroup].map((tag) => tag.key)
          })

          pagePromises.push(tooManagement.searchVacancies(filter, page))
        } else {
          const params = {
            filter: this.searchTerm,
            includeArchived: this.filter.includeArchived,
            ...page,
          }
          pagePromises.push(tooPublishers.getVacancies(params))
        }
      }

      const vacancies = []
      try {
        const results = await Promise.all(pagePromises)
        results.forEach((result) => {
          vacancies.push(...result.data)
        })
        return vacancies
      } catch (error) {
        this.error = error
      }
    },
  },
})
